import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  alpha,
  useTheme,

  Chip,
  Paper,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import WSTextField, { CurrencyType, TextType } from "../../../Components/TextBox/WSTextBox";
import WSSelectField from "../../../Components/Select/WSSelect";
import WSButton from "../../../Components/Button/WSButton";
import config from "../../../Config/clientConfig.json"
import { Add, Category, Discount, TrendingUp } from "@mui/icons-material";
import { callAPI, clearRequest, Method } from "../../../commonFunctions/CommonFunctions";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";

type RewardRuleFormValues = {
  name: string;
  conditionType: "points" | "bill";
  threshold: number;
  rewardType: "product" | "cashback";
  thresholdType: 'single' | 'between';
  minCashback?: number;
  maxCashback?: number;
  minThreshold?: number;
  maxThreshold?: number;
  productList?: string[];
  isActive: boolean;
};

const RewardRule = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showMessage } = useSnackbar();
  const { handleSubmit, control, setValue, watch } = useForm<RewardRuleFormValues>({
    defaultValues: {
      name: "",
      conditionType: "points",
      threshold: 200,
      minThreshold: 100,
      maxThreshold: 200,
      thresholdType: 'single',
      rewardType: "product",
      minCashback: 1,
      maxCashback: 10,
      productList: [],
      isActive: true,
    },
  });

  const thresholdType = watch('thresholdType')

  const source = {
    url: "products/search",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: []
  };

  const [loading, setLoading] = useState(false);
  const [rewardType, setRewardType] = useState<"cashback" | "product">("product")
  const [products, setProducts] = useState<string[]>([]);
  const [productInput, setProductInput] = useState("");

  useEffect(() => {
    /**
     * Edit Function
     * @param id
     *
     */
    async function getProductById(id: any) {
      try {
        setLoading(true)
        const response: any = await callAPI(`rewardRules/getBy`, Method.GET, {}, id);

        if (response && response?.data) {
          setValue("name", response.data.name);
          setValue("conditionType", response.data.conditionType);
          setValue("maxCashback", response.data.brand._id);
          setValue("minCashback", response.data.discountPrice);
          setValue("productList", response.data.description);
          setValue("threshold", response.data.stock);
        }
      } catch (error) {
        console.error("failed to retrieve data", error);
      } finally {
        setLoading(false)
      }
    }
    if (id) {
      getProductById(id);
    }
  }, [id, setValue]);

  const addProduct = () => {
    if (productInput.trim()) {
      setProducts((prev) => [...prev, productInput.trim()]);
      setProductInput("");
    }
  };

  const removeProduct = (prod: string) => {
    setProducts((prev) => prev.filter((p) => p !== prod));
  };

  const handleProductChange = (event: any) => {

    setProductInput(event.target.value)
  };

  const transFormData = (data: any) => {
    let bannerList: Array<any> = [];
    data?.forEach((item: any) => {
      bannerList.push({
        label: item.name,
        value: item._id,
        fullProduct: item // Optional: keep full original object if needed
      });
    });
    return bannerList;
  };

  const handleChangeType = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {

    setRewardType(event.target.value as 'cashback' | 'product');
  };


  const handleData = async (data: any) => {
    try {
      const request = {
        name: data.ruleName,
        rewardType,
        conditionType: data.conditionType,
        thresholdType: data.thresholdType,
        minCashback: data.minCashback,
        maxCashback: data.maxCashback,
        triggerMin: data.minThreshold,
        triggerMax: data.maxThreshold,
        threshold:data.threshold,
        description: data.description,
        products: products
      };

      if (id) {
        const response: any = await callAPI(`rewardRules/update/${id}`, Method.PUT, request);
        if (response.message) {
          showMessage(response?.message, response.success === true ? "success" : "error");
          if (response.success === true) {
            navigate('/reward/rule/list')
          }
        }

      } else {
        const response: any = await callAPI("rewardRules/create", Method.POST, request);
        if (response.message) {
          showMessage(response?.message, response.success === true ? "success" : "error");
          if (response.success === true) {
            navigate('/reward/rule/list')
          }
        }
      }
    } catch (e: any) {
      showMessage(e?.response?.data?.message || "something went wrong", 'error');
    }
  };

  const handleCancelClick = () => {
    navigate('/reward/list')
  }

  return (
    <Box sx={{ mx: 'auto', p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          backdropFilter: 'blur(10px)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Discount
            sx={{
              color: theme.palette.primary.main,
              fontSize: 32,
              mr: 2
            }}
          />
          <Typography variant="h4" fontWeight="600" color="primary">
            Create Reward Rule
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Configure rules to automatically reward your customers based on their activities
        </Typography>

        <form onSubmit={handleSubmit(handleData)} noValidate>
          <Grid item xs={12} sm={6} sx={{ mb: 5, display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <WSButton
              id="cancel"
              label="Cancel"
              variant="outlined"
              size="small"
              sx={{ mr: 2 }}
              loading={loading}
              onClick={handleCancelClick}
            />
            <WSButton
              id="createRule"
              label="Create Rule"
              type="submit"
              size="small"
              loading={loading}
            />
          </Grid>
          <Grid container spacing={4}>
            {/* Rule Details Section */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: alpha(theme.palette.background.paper, 0.7)
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Rule Details
                  </Typography>

                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <WSTextField
                        id="ruleName"
                        label="Rule Name"
                        control={control}
                        rules={{ required: true }}
                        name="ruleName"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <WSSelectField
                        id="conditionType"
                        control={control}
                        options={config.rewardConditionType.staticOptionData}
                        rules={{ required: true }}
                        label="Condition Type"
                        name="conditionType"
                        defaultValue={config.rewardConditionType.staticOptionData[0]}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <WSSelectField
                        id="thresholdType"
                        control={control}
                        options={config.thresholdType.staticOptionData}
                        rules={{ required: true }}
                        label="Threshold Type"
                        name="thresholdType"
                        defaultValue={config.thresholdType.staticOptionData[0]}
                      />
                    </Grid>

                    {
                      thresholdType === 'between' ? <React.Fragment>
                        <Grid item xs={12} sm={6}>
                          <WSTextField
                            id="minThreshold"
                            label="Min Threshold"
                            type={TextType.Number}
                            rules={thresholdType === 'between' ? { required: true } : { required: false }}
                            name="minThreshold"
                            control={control}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <WSTextField
                            id="maxThreshold"
                            label="Max Threshold"
                            type={TextType.Number}
                            rules={thresholdType === 'between' ? { required: true } : { required: false }}
                            name="maxThreshold"
                            control={control}
                          />
                        </Grid>
                      </React.Fragment> :
                        <Grid item xs={12}>
                          <WSTextField
                            id="threshold"
                            label="Threshold"
                            type={TextType.Number}
                            rules={thresholdType === 'single' ? { required: true } : { required: false }}
                            name="threshold"
                            control={control}
                          />
                        </Grid>
                    }

                    <Grid item xs={12}>
                      <WSTextField
                        id="description"
                        label="Short Description"
                        control={control}
                        size="medium"
                        name="description"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Reward Configuration Section */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  background: alpha(theme.palette.background.paper, 0.7)
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp
                      sx={{
                        color: theme.palette.secondary.main,
                        mr: 1.5
                      }}
                    />
                    <Typography variant="h6" fontWeight="600">
                      Reward Configuration
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <WSSelectField
                        id="rewardType"
                        options={config.rewardType.staticOptionData}
                        label="Reward Type"
                        name="rewardType"
                        control={control}
                        onChange={handleChangeType}
                        rules={{ required: "Reward type is required" }}
                      />
                    </Grid>

                    {/* Conditionally render fields based on rewardType */}
                    {rewardType === 'cashback' ? (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                          Cashback Range (₹)
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <WSTextField
                              id="minCashback"
                              label="Min Cashback"
                              type={TextType.Currency}
                              currency={CurrencyType.Rupee}

                              name="minCashback"
                              control={control}
                              rules={{
                                min: { value: 0, message: "Cannot be negative" }
                              }}

                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <WSTextField
                              id="maxCashback"
                              label="Max Cashback"
                              type={TextType.Currency}
                              currency={CurrencyType.Rupee}
                              name="maxCashback"
                              control={control}
                              rules={{
                                min: { value: 0, message: "Cannot be negative" },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : rewardType === 'product' ? (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Category
                            sx={{
                              color: theme.palette.info.main,
                              mr: 1.5
                            }}
                          />
                          <Typography variant="subtitle2">
                            Product List
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <WSSelectField
                            label="Select Product"
                            name="productName"
                            control={control}
                            rules={{ required: true }}
                            source={source}
                            transFormData={transFormData}
                            onChange={handleProductChange}
                            id="productName"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.primary.light, 0.05),
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                              }
                            }}
                          />

                          <WSButton
                            id="addBtn"
                            label="Add"
                            size="medium"
                            onClick={addProduct}
                            startIcon={<Add />}
                            sx={{ minWidth: '90px' }}
                          />
                        </Box>

                        {products.length > 0 && (
                          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {products.map((p) => (
                              <Chip
                                key={p}
                                label={p}
                                onDelete={() => removeProduct(p)}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      </Grid>
                    ) : (
                      // Add additional reward type UIs here as needed
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Configure your discount settings here.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box >
  );
};

export default RewardRule;